import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserPlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSubscriptionLimits } from "@/hooks/useSubscriptionLimits";
import { ProUpgradeModal } from "./ProUpgradeModal";

const clientSchema = z.object({
  name: z.string()
    .trim()
    .min(1, "Nome é obrigatório")
    .max(100, "Nome deve ter no máximo 100 caracteres"),
  phone: z.string()
    .trim()
    .optional()
    .refine((val) => !val || /^\(\d{2}\)\s?\d{4,5}-?\d{4}$/.test(val) || /^\d{10,11}$/.test(val), {
      message: "Formato inválido. Use (11) 98765-4321 ou 11987654321"
    }),
  cpf: z.string()
    .trim()
    .optional()
    .refine((val) => !val || /^\d{11}$/.test(val.replace(/\D/g, "")), {
      message: "CPF deve conter 11 dígitos"
    })
});

interface AddClientDialogProps {
  onSuccess: () => void;
  trigger?: React.ReactNode;
}

export function AddClientDialog({ onSuccess, trigger }: AddClientDialogProps) {
  const [open, setOpen] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const queryClient = useQueryClient();
  const { maxClients, isPro } = useSubscriptionLimits();

  // Query to count current clients
  const { data: clientsCount = 0 } = useQuery({
    queryKey: ["clients-count"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      const { count, error } = await supabase
        .from("clients")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);

      if (error) return 0;
      return count || 0;
    },
  });

  const isLimitReached = !isPro && maxClients !== null && clientsCount >= maxClients;

  const form = useForm<z.infer<typeof clientSchema>>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      name: "",
      phone: "",
      cpf: "",
    },
  });

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen && isLimitReached) {
      setShowProModal(true);
      return;
    }
    setOpen(newOpen);
  };

  const handleSubmit = async (values: z.infer<typeof clientSchema>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      const { error } = await supabase.from("clients").insert({
        user_id: user.id,
        name: values.name,
        phone: values.phone || null,
        cpf: values.cpf || null,
      });

      if (error) throw error;

      toast.success("Cliente cadastrado com sucesso!");
      setOpen(false);
      form.reset();
      // Invalidate queries to refresh client list and count
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["clients-count"] });
      onSuccess();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="outline" className="gap-2">
              <UserPlus className="h-4 w-4" />
              Cadastrar Cliente
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Novo Cliente</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4" autoComplete="off">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome Completo</FormLabel>
                    <FormControl>
                      <Input placeholder="João Silva" autoComplete="off" autoCorrect="off" autoCapitalize="words" spellCheck={false} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefone (opcional)</FormLabel>
                    <FormControl>
                      <Input placeholder="(11) 98765-4321" autoComplete="off" inputMode="tel" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cpf"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CPF (opcional)</FormLabel>
                    <FormControl>
                      <Input placeholder="12345678900" autoComplete="off" inputMode="numeric" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? "Cadastrando..." : "Cadastrar Cliente"}
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <ProUpgradeModal 
        open={showProModal} 
        onOpenChange={setShowProModal} 
        limitType="clients" 
      />
    </>
  );
}
